import { Badge, Box, Button, Center, Divider, Flex, Text, Textarea } from '@chakra-ui/react'
import { LaborEntryType, NarrativeCategory } from '@hb/shared'
import { useCallback, useEffect, useState } from 'react'
import { useField } from 'react-final-form'
import { LaborEntryComponentProps } from '../types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface DotPhrase {
  id: string
  label: string
  category: NarrativeCategory
  text: string
}

// ─── Dot Phrases ──────────────────────────────────────────────────────────────

const DOT_PHRASES: DotPhrase[] = [
  {
    id: 'position_change',
    label: 'Position Change',
    category: NarrativeCategory.GENERAL,
    text: 'Patient assisted into [Left Side-Lying / Hands and Knees / Squatting / Semi-Fowler] position for comfort and fetal descent. Tolerating well. FHR stable.',
  },
  {
    id: 'hydrotherapy',
    label: 'Hydrotherapy',
    category: NarrativeCategory.GENERAL,
    text: 'Patient entered hydrotherapy (tub/shower) for pain management. Water temperature 98–100°F. Continuous monitoring maintained. FHR reassuring.',
  },
  {
    id: 'ambulation',
    label: 'Ambulation',
    category: NarrativeCategory.GENERAL,
    text: 'Patient ambulating in hallway with support for [10 / 20 / 30] minutes. Tolerating activity well. Contractions [unchanged / increased in frequency]. Returned to room without difficulty.',
  },
  {
    id: 'fhr_decel',
    label: 'FHR Deceleration',
    category: NarrativeCategory.EMERGENCY,
    text: 'Variable deceleration noted to [BPM] at [TIME]. Patient repositioned to left lateral. Oxygen applied at 10 L/min via non-rebreather mask. IV fluid bolus initiated. Deceleration resolved within [minutes]. Provider [Name] notified.',
  },
  {
    id: 'iv_placement',
    label: 'IV Placement',
    category: NarrativeCategory.PROCEDURE,
    text: 'Peripheral IV placed in [left/right] [antecubital/forearm/hand] with [18/20/22]g catheter on first attempt. Site without swelling, redness, or infiltration. IV flushed with 10 mL normal saline without difficulty.',
  },
  {
    id: 'consent',
    label: 'Informed Consent',
    category: NarrativeCategory.CONSENT,
    text: 'Informed consent obtained for [procedure/intervention]. Risks, benefits, and alternatives explained. Patient verbalized understanding and agreed to proceed. Questions answered to patient satisfaction.',
  },
  {
    id: 'family_update',
    label: 'Family Update',
    category: NarrativeCategory.FAMILY_COMM,
    text: 'Family/support persons updated on patient status at [TIME]. [Patient name] doing well, labor progressing. Questions answered. Emotional support provided.',
  },
  {
    id: 'pushing_begins',
    label: 'Pushing Begins',
    category: NarrativeCategory.GENERAL,
    text: 'Patient began active pushing at [TIME]. Cervix completely dilated and effaced. Fetal station [station]. FHR [reassuring / with variable decels managed with repositioning]. Coach at bedside providing support.',
  },
]

// ─── Category Meta ────────────────────────────────────────────────────────────

const CATEGORY_META: Record<NarrativeCategory, { label: string; color: string; scheme: string }> = {
  [NarrativeCategory.GENERAL]: { label: 'General', color: '#4A90D9', scheme: 'blue' },
  [NarrativeCategory.PROCEDURE]: { label: 'Procedure', color: '#805AD5', scheme: 'purple' },
  [NarrativeCategory.CONSENT]: { label: 'Consent', color: '#38A169', scheme: 'green' },
  [NarrativeCategory.EMERGENCY]: { label: 'Emergency', color: '#E53E3E', scheme: 'red' },
  [NarrativeCategory.FAMILY_COMM]: { label: 'Family Comm', color: '#D69E2E', scheme: 'yellow' },
}

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Amendment Banner ─────────────────────────────────────────────────────────

const AmendmentBanner = () => {
  const { input } = useField<boolean>('isAmended', { subscription: { value: true } })
  const { input: parentInput } = useField<string>('parentNoteId', { subscription: { value: true } })

  if (!input.value) return null

  return (
    <Flex
      w="100%"
      align="center"
      gap={3}
      bg="orange.50"
      _dark={{ bg: 'rgba(237,137,54,0.12)' }}
      border="1px solid"
      borderColor="orange.300"
      borderRadius={12}
      px={4}
      py={3}>
      <Flex flexFlow="column" gap={0.5} flex={1}>
        <Text fontSize="sm" fontWeight={700} fontFamily="hero-new" color="orange.700">
          ✏ Amendment
        </Text>
        <Text fontSize="xs" fontFamily="hero-new" color="orange.600">
          This note amends{' '}
          {parentInput.value ? `note ${parentInput.value.slice(0, 8)}…` : 'a prior entry'}. The
          original will be preserved with strikethrough.
        </Text>
      </Flex>
      <Button
        size="xs"
        variant="ghost"
        colorScheme="orange"
        fontFamily="hero-new"
        onClick={() => input.onChange(false)}>
        Remove
      </Button>
    </Flex>
  )
}

// ─── Category Selector ────────────────────────────────────────────────────────

const CategorySelector = () => {
  const { input } = useField<NarrativeCategory>('category', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>CATEGORY</SectionLabel>
      <Flex gap={2} w="100%" wrap="wrap">
        {(Object.values(NarrativeCategory) as NarrativeCategory[]).map(cat => {
          const meta = CATEGORY_META[cat]
          const isActive = input.value === cat
          return (
            <Button
              key={cat}
              flex={1}
              minW="80px"
              h="44px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? meta.scheme : 'gray'}
              borderRadius={10}
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              fontSize="sm"
              onClick={() => input.onChange(cat)}>
              {meta.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Tag Selector ─────────────────────────────────────────────────────────────

const TAGS = [
  { id: 'position_change', label: 'Position Change' },
  { id: 'hydrotherapy', label: 'Hydrotherapy' },
  { id: 'aromatherapy', label: 'Aromatherapy' },
  { id: 'ambulation', label: 'Ambulation' },
  { id: 'iv_placement', label: 'IV Placement' },
  { id: 'fetal_monitoring', label: 'Fetal Monitoring' },
]

const TagSelector = () => {
  const { input } = useField<string[]>('tags', { subscription: { value: true } })
  const selected = input.value ?? []

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      input.onChange(selected.filter(t => t !== id))
    } else {
      input.onChange([...selected, id])
    }
  }

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>TAGS</SectionLabel>
      <Flex gap={2} wrap="wrap" w="100%">
        {TAGS.map(tag => {
          const isActive = selected.includes(tag.id)
          return (
            <Button
              key={tag.id}
              h="36px"
              px={3}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'teal' : 'gray'}
              borderRadius="full"
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              fontSize="sm"
              onClick={() => toggle(tag.id)}>
              {tag.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Dot Phrase Bar ───────────────────────────────────────────────────────────

interface DotPhraseBarProps {
  activeCategory: NarrativeCategory | undefined
  onInsert: (text: string) => void
}

const DotPhraseBar = ({ activeCategory, onInsert }: DotPhraseBarProps) => {
  const filtered = activeCategory
    ? DOT_PHRASES.filter(p => p.category === activeCategory)
    : DOT_PHRASES

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <Flex align="center" gap={2}>
        <SectionLabel>SMART TEMPLATES</SectionLabel>
        <Badge colorScheme="teal" borderRadius="full" fontSize="10px" fontFamily="hero-new" px={2}>
          Tap to insert
        </Badge>
      </Flex>
      <Flex gap={2} wrap="wrap" w="100%">
        {filtered.map(phrase => {
          const meta = CATEGORY_META[phrase.category]
          return (
            <Button
              key={phrase.id}
              h="36px"
              px={3}
              variant="outline"
              borderColor={meta.color}
              color={meta.color}
              _hover={{ bg: meta.color, color: 'white' }}
              borderRadius={8}
              fontFamily="hero-new"
              fontWeight={500}
              fontSize="sm"
              onClick={() => onInsert(phrase.text)}>
              {phrase.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Note Body Input ──────────────────────────────────────────────────────────

interface NoteBodyInputProps {
  pendingInsert: string | null
  onInsertConsumed: () => void
}

const NoteBodyInput = ({ pendingInsert, onInsertConsumed }: NoteBodyInputProps) => {
  const { input } = useField<string>('noteBody', { subscription: { value: true } })
  const [isFocused, setIsFocused] = useState(false)

  // When a dot-phrase is pending, append it to the existing note body
  useEffect(() => {
    if (!pendingInsert) return
    const existing = input.value ?? ''
    const joined = existing ? `${existing}\n\n${pendingInsert}` : pendingInsert
    input.onChange(joined)
    onInsertConsumed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInsert])

  const charCount = (input.value ?? '').length

  return (
    <Flex flexFlow="column" w="100%" gap={1}>
      <Flex justify="space-between" align="baseline">
        <SectionLabel>NOTE</SectionLabel>
        <Text fontSize="xs" fontFamily="hero-new" color={charCount > 2000 ? 'red.400' : 'gray.400'}>
          {charCount} chars
        </Text>
      </Flex>
      <Box
        border="2px solid"
        borderColor={isFocused ? 'teal.400' : 'gray.200'}
        _dark={{ borderColor: isFocused ? 'teal.400' : 'gray.600' }}
        borderRadius={12}
        overflow="hidden"
        transition="border-color 0.15s">
        <Textarea
          value={input.value ?? ''}
          onChange={e => input.onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Begin typing your clinical note, or tap a Smart Template above to insert a pre-filled phrase…"
          fontFamily="hero-new"
          fontSize="md"
          lineHeight={1.6}
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          border="none"
          _focus={{ boxShadow: 'none' }}
          resize="vertical"
          minH="180px"
          p={3}
        />
      </Box>
    </Flex>
  )
}

// ─── Amendment Toggle ─────────────────────────────────────────────────────────

// const AmendmentToggle = () => {
//   const { input } = useField<boolean>('isAmended', { subscription: { value: true } })

//   if (input.value) return null

//   return (
//     <Collapse unmountOnExit in={!input.value}>
//       <Flex
//         w="100%"
//         align="center"
//         justify="space-between"
//         gap={3}
//         border="1px dashed"
//         borderColor="gray.300"
//         _dark={{ borderColor: 'gray.600' }}
//         borderRadius={12}
//         px={4}
//         py={3}>
//         <Flex flexFlow="column" gap={0.5}>
//           <Text
//             fontSize="sm"
//             fontWeight={600}
//             fontFamily="hero-new"
//             color="gray.600"
//             _dark={{ color: 'gray.300' }}>
//             Amending a prior note?
//           </Text>
//           <Text fontSize="xs" fontFamily="hero-new" color="gray.400">
//             Original will be preserved with strikethrough for audit trail.
//           </Text>
//         </Flex>
//         <Button
//           size="sm"
//           variant="outline"
//           colorScheme="orange"
//           borderRadius={8}
//           fontFamily="hero-new"
//           fontWeight={600}
//           onClick={() => input.onChange(true)}>
//           Mark as Amendment
//         </Button>
//       </Flex>
//     </Collapse>
//   )
// }

// ─── Main Component ───────────────────────────────────────────────────────────

export const NarrativeEntry = ({
  existingEntry,
}: LaborEntryComponentProps<LaborEntryType.NARRATIVE>) => {
  const { input: categoryInput } = useField<NarrativeCategory>('category', {
    subscription: { value: true },
  })

  const [pendingInsert, setPendingInsert] = useState<string | null>(null)

  const handleDotPhraseInsert = useCallback((text: string) => {
    setPendingInsert(text)
  }, [])

  return (
    <Center pb={8} w="100%">
      <Flex flexFlow="column" w="100%" maxW="560px" py={4} px={2} gap={5} align="center">
        {existingEntry ? <AmendmentBanner /> : null}
        <CategorySelector />
        <Divider />
        <DotPhraseBar
          activeCategory={categoryInput.value || undefined}
          onInsert={handleDotPhraseInsert}
        />
        <Divider />
        <NoteBodyInput
          pendingInsert={pendingInsert}
          onInsertConsumed={() => setPendingInsert(null)}
        />
        <TagSelector />
        {/* <Divider /> */}
        {/* <AmendmentToggle /> */}
      </Flex>
    </Center>
  )
}
